<template>
  <div class="my-past-travel">
    <h2 class="my-past-travel__title">{{ $t('rebooking.pastTravel') }}</h2>
    <section class="my-past-travel__journey">
      <h3 class="my-past-travel__journey-type">
        {{ $t('booking.outbound') }}
      </h3>
      <p class="my-past-travel__od">
        {{ departureStation }} - {{ arrivalStation }}
      </p>
      <p class="my-past-travel__date">{{ formattedDepartureDay }}</p>
      <p class="my-past-travel__time">{{ formattedDepartureTime }}</p>
    </section>

    <section
      v-if="hasReturnJourney"
      class="my-past-travel__journey my-past-travel__journey--return"
    >
      <h3 class="my-past-travel__journey-type">
        {{ $t('booking.inbound') }}
      </h3>
      <p class="my-past-travel__od">
        {{ returnDepartureStation }} - {{ returnArrivalStation }}
      </p>
      <p class="my-past-travel__date">{{ formattedReturnDay }}</p>
      <p class="my-past-travel__time">{{ formattedReturnTime }}</p>
    </section>

    <div class="my-past-travel__details">
      <div class="my-past-travel__detail">
        <span>
          <svg class="my-past-travel__icon clr-secondary">
            <use :xlink:href="`#${ticket}`" />
          </svg>
          {{ ticketType }}
        </span>
        <span>{{ ticketPrice }}</span>
      </div>
      <div
        v-if="hasSeatSelection"
        class="my-past-travel__detail"
      >
        <span>
          <svg class="my-past-travel__icon clr-secondary">
            <use :xlink:href="`#${seat}`" />
          </svg>
          {{ seatProductName }}
        </span>
        <span>{{ seatPrice }}</span>
      </div>

      <div
        v-for="product in nonSeatProducts"
        :key="product.code"
        class="my-past-travel__detail"
      >
        <span>
          <svg class="my-past-travel__icon clr-secondary">
            <use :xlink:href="`#${getIconForProduct(product)}`" />
          </svg>
          {{ product.name }}
        </span>
        <span>
          {{ formatNormalizedPrice(normalizePrice(product.totalPrice)) }}
        </span>
      </div>

      <!-- <div
        v-if="totalFeesAmount !== '0,-'"
        class="my-past-travel__detail"
      >
        <span>
          <svg class="my-past-travel__icon clr-secondary">
            <use :xlink:href="`#${fee}`" />
          </svg>
          Endringsgebyr
        </span>
        <span>{{ totalFeesAmount }}</span>
      </div> -->
    </div>

    <div class="my-past-travel__total">
      <span>{{ $t('rebooking.totalPaid') }}</span>
      <span>{{ totalWithoutFees }}</span>
    </div>
    <!-- <div class="my-past-travel__total-without-fees">
      <span>Total uten gebyr</span>
      <span>{{ totalWithoutFees }}</span>
    </div> -->
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useBookingStore } from '@/stores/bookingStore'
  import { formatShortNorwegianDate, formatTime } from '@/utils/dateFormatter'
  import {
    normalizePrice,
    formatNormalizedPrice,
  } from '@/utils/priceNormalization'
  import ticket from '@/assets/icons/ticket.svg'
  import luggage from '@/assets/icons/luggage.svg'
  import seat from '@/assets/icons/seat.svg'
  import discount from '@/assets/icons/discount.svg'
  import fee from '@/assets/icons/fee.svg'
  import bike from '@/assets/icons/bikeRed.svg'
  import type {
    Booking,
    Segment,
    BookingProduct as Product,
    Fee,
    BookingJourneySegment,
  } from '@/types/bookingTypes'

  const { t } = useI18n()
  const bookingStore = useBookingStore()

  const booking = computed(() => bookingStore.bookingData as unknown as Booking)

  const cancelledOutboundSegment = computed(() => {
    if (!booking.value?.outbound_booking_tariff_segments) return null

    return booking.value.outbound_booking_tariff_segments.find((segment) => {
      return segment.required_products?.some(
        (product: Product) =>
          product.provisional === true && product.cancelled === true
      )
    })
  })

  const provisionalCancelledSegments = computed(() => {
    const segments: Segment[] = []

    if (booking.value?.outbound_booking_tariff_segments) {
      booking.value.outbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          const hasProvisionalCancelledRequiredProducts = segment.required_products?.some(
            (product: Product) =>
              product.provisional === true && product.cancelled === true
          )
          
          const hasProvisionalCancelledAdditionalProducts = segment.additional_products?.some(
            (product: Product) =>
              product.provisional === true && product.cancelled === true
          )
          
          if (hasProvisionalCancelledRequiredProducts || hasProvisionalCancelledAdditionalProducts) {
            segments.push(segment)
          }
        }
      )
    }

    if (booking.value?.inbound_booking_tariff_segments) {
      booking.value.inbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          const hasProvisionalCancelledRequiredProducts = segment.required_products?.some(
            (product: Product) =>
              product.provisional === true && product.cancelled === true
          )
          
          const hasProvisionalCancelledAdditionalProducts = segment.additional_products?.some(
            (product: Product) =>
              product.provisional === true && product.cancelled === true
          )
          
          if (hasProvisionalCancelledRequiredProducts || hasProvisionalCancelledAdditionalProducts) {
            segments.push(segment)
          }
        }
      )
    }

    return segments
  })

  const bookingFees = computed(() => {
    const fees: Fee[] = []

    if (booking.value?.outbound_booking_tariff_segments) {
      booking.value.outbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          segment.fees?.forEach((fee: Fee) => {
            if (provisionalCancelledSegments.value.includes(segment)) {
              if (
                fee.provisional === false ||
                (fee.provisional === true && fee.cancelled === true)
              ) {
                fees.push(fee)
              }
            } else {
              if (
                fee.code === 'CHANGE' &&
                !fee.cancelled &&
                fee.provisional === false
              ) {
                fees.push(fee)
              }
            }
          })
        }
      )
    }

    if (booking.value?.inbound_booking_tariff_segments) {
      booking.value.inbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          segment.fees?.forEach((fee: Fee) => {
            if (provisionalCancelledSegments.value.includes(segment)) {
              if (
                fee.provisional === false ||
                (fee.provisional === true && fee.cancelled === true)
              ) {
                fees.push(fee)
              }
            } else {
              if (
                fee.code === 'CHANGE' &&
                !fee.cancelled &&
                fee.provisional === false
              ) {
                fees.push(fee)
              }
            }
          })
        }
      )
    }

    if (booking.value?.fees) {
      booking.value.fees.forEach((fee: Fee) => {
        if (
          fee.provisional === false ||
          (fee.provisional === true && fee.cancelled === true)
        ) {
          fees.push(fee)
        }
      })
    }

    return fees
  })

  const getSortedSegments = (segments: Segment[]) => {
    return [...segments].sort(
      (a, b) => (a.sequence_number || 0) - (b.sequence_number || 0)
    )
  }

  const outboundPastSegments = computed(() => {
    if (!booking.value?.outbound_booking_tariff_segments) return []
    return getSortedSegments(
      booking.value.outbound_booking_tariff_segments.filter((seg) =>
        seg.required_products?.some(
          (product: any) =>
            product.provisional === true && product.cancelled === true
        )
      )
    )
  })
  const inboundPastSegments = computed(() => {
    if (!booking.value?.inbound_booking_tariff_segments) return []
    return getSortedSegments(
      booking.value.inbound_booking_tariff_segments.filter((seg) =>
        seg.required_products?.some(
          (product: any) =>
            product.provisional === true && product.cancelled === true
        )
      )
    )
  })

  const departureStation = computed(() => {
    const seg = outboundPastSegments.value[0]
    return seg?.departure_station?.name || ''
  })
  const arrivalStation = computed(() => {
    const seg =
      outboundPastSegments.value[outboundPastSegments.value.length - 1]
    return seg?.arrival_station?.name || ''
  })

  const formattedDepartureDay = computed(() => {
    const seg = outboundPastSegments.value[0]
    const journeySeg = seg?.booking_journey_segments?.[0]
    if (!journeySeg) return ''
    const departureDateTime = journeySeg.departure_date_time
    if (!departureDateTime) return ''
    const date = formatShortNorwegianDate(departureDateTime)
    const year = new Date(departureDateTime).getFullYear()
    return `${date} ${year}`
  })
  const formattedDepartureTime = computed(() => {
    const firstSeg = outboundPastSegments.value[0]
    const lastSeg =
      outboundPastSegments.value[outboundPastSegments.value.length - 1]
    const firstJourney = firstSeg?.booking_journey_segments?.[0] as
      | BookingJourneySegment
      | undefined
    const lastJourney =
      lastSeg?.booking_journey_segments &&
      lastSeg.booking_journey_segments.length > 0
        ? (lastSeg.booking_journey_segments[
            lastSeg.booking_journey_segments.length - 1
          ] as BookingJourneySegment)
        : undefined
    if (!firstJourney || !lastJourney) return ''
    const departureTime = formatTime(firstJourney.departure_date_time)
    const arrivalTime = formatTime(lastJourney.arrival_date_time)

    const dep = new Date(firstJourney.departure_date_time)
    const arr = new Date(lastJourney.arrival_date_time)
    const diffMs = arr.getTime() - dep.getTime()
    const hours = Math.floor(diffMs / 1000 / 60 / 60)
    const minutes = Math.floor((diffMs / 1000 / 60) % 60)
    return `${departureTime}-${arrivalTime} ${hours}t${
      minutes > 0 ? ' ' + minutes + 'min' : ''
    }`
  })

  const hasReturnJourney = computed(() => {
    return inboundPastSegments.value.length > 0
  })

  const hasSeatSelection = computed(() => {
    const allSegments = [
      ...outboundPastSegments.value,
      ...inboundPastSegments.value,
    ]
    return allSegments.some((seg) =>
      seg.additional_products?.some(
        (product: any) =>
          product.type === 'SF' &&
          product.provisional === true &&
          product.cancelled === true
      )
    )
  })

  const returnDepartureStation = computed(() => {
    const seg = inboundPastSegments.value[0]
    return seg?.departure_station?.name || ''
  })
  const returnArrivalStation = computed(() => {
    const seg = inboundPastSegments.value[inboundPastSegments.value.length - 1]
    return seg?.arrival_station?.name || ''
  })
  const formattedReturnDay = computed(() => {
    const seg = inboundPastSegments.value[0]
    const journeySeg = seg?.booking_journey_segments?.[0]
    if (!journeySeg) return ''
    const departureDateTime = journeySeg.departure_date_time
    if (!departureDateTime) return ''
    const date = formatShortNorwegianDate(departureDateTime)
    const year = new Date(departureDateTime).getFullYear()
    return `${date} ${year}`
  })
  const formattedReturnTime = computed(() => {
    const firstSeg = inboundPastSegments.value[0]
    const lastSeg =
      inboundPastSegments.value[inboundPastSegments.value.length - 1]
    const firstJourney = firstSeg?.booking_journey_segments?.[0] as
      | BookingJourneySegment
      | undefined
    const lastJourney =
      lastSeg?.booking_journey_segments &&
      lastSeg.booking_journey_segments.length > 0
        ? (lastSeg.booking_journey_segments[
            lastSeg.booking_journey_segments.length - 1
          ] as BookingJourneySegment)
        : undefined
    if (!firstJourney || !lastJourney) return ''
    const departureTime = formatTime(firstJourney.departure_date_time)
    const arrivalTime = formatTime(lastJourney.arrival_date_time)

    const dep = new Date(firstJourney.departure_date_time)
    const arr = new Date(lastJourney.arrival_date_time)
    const diffMs = arr.getTime() - dep.getTime()
    const hours = Math.floor(diffMs / 1000 / 60 / 60)
    const minutes = Math.floor((diffMs / 1000 / 60) % 60)
    return `${departureTime}-${arrivalTime} ${hours}t${
      minutes > 0 ? ' ' + minutes + 'min' : ''
    }`
  })

    const confirmedFees = computed(() => {
    const fees: Fee[] = []

    if (booking.value?.outbound_booking_tariff_segments) {
      booking.value.outbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          segment.fees?.forEach((fee: Fee) => {
            if (fee.provisional === false) {
              fees.push(fee)
            }
          })
        }
      )
    }

    if (booking.value?.inbound_booking_tariff_segments) {
      booking.value.inbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          segment.fees?.forEach((fee: Fee) => {
            if (fee.provisional === false) {
              fees.push(fee)
            }
          })
        }
      )
    }

    if (booking.value?.fees) {
      booking.value.fees.forEach((fee: Fee) => {
        if (fee.provisional === false) {
          fees.push(fee)
        }
      })
    }

    return fees
  })

  const ticketType = computed(() => {
    const seg = provisionalCancelledSegments.value.find((s) =>
      s.required_products?.some(
        (product: Product) =>
          product.provisional === true && product.cancelled === true
      )
    )
    if (!seg?.required_products?.[0]) return ''
    return seg.required_products[0].name || 'Standard'
  })
  const ticketPrice = computed(() => {
    let totalTicketPrice = 0
    provisionalCancelledSegments.value.forEach((segment: Segment) => {
      segment.required_products?.forEach((product: Product) => {
        if (product.provisional === true && product.cancelled === true) {
          const price = product.price || 0
          if (
            typeof price === 'object' &&
            price !== null &&
            'amount' in price
          ) {
            totalTicketPrice += (price as { amount: number }).amount || 0
          } else if (typeof price === 'number') {
            totalTicketPrice += price
          }
        }
      })
    })
    return formatNormalizedPrice(normalizePrice(totalTicketPrice))
  })
  const seatProducts = computed(() => {
    const products: Product[] = []
    provisionalCancelledSegments.value.forEach((segment: Segment) => {
      segment.additional_products?.forEach((product: Product) => {
        if (
          product.type === 'SF' &&
          product.provisional === true &&
          product.cancelled === true
        ) {
          products.push(product)
        }
      })
    })
    return products
  })
  
  const seatProductName = computed(() => {
    if (seatProducts.value.length === 0) return t('rebooking.seats')
    
    const firstProductName = seatProducts.value[0].name
    const allSameName = seatProducts.value.every(product => product.name === firstProductName)
    
    if (allSameName) {
      return firstProductName
    } else {
      return t('rebooking.seats')
    }
  })
  
  const seatPrice = computed(() => {
    if (!hasSeatSelection.value) return '0,-'
    let totalSeatPrice = 0
    seatProducts.value.forEach((product: Product) => {
      const price = product.price || 0
      if (
        typeof price === 'object' &&
        price !== null &&
        'amount' in price
      ) {
        totalSeatPrice += (price as { amount: number }).amount || 0
      } else if (typeof price === 'number') {
        totalSeatPrice += price
      }
    })
    return formatNormalizedPrice(normalizePrice(totalSeatPrice))
  })
  const nonSeatProducts = computed(() => {
    const productMap = new Map<
      string,
      Product & { count: number; totalPrice: number }
    >()
    provisionalCancelledSegments.value.forEach((segment: Segment) => {
      segment.additional_products?.forEach((product: Product) => {
        if (
          product.type !== 'SF' &&
          product.provisional === true &&
          product.cancelled === true
        ) {
          const key = product.tariff_code || product.code
          if (productMap.has(key)) {
            const existingProduct = productMap.get(key)!
            const price = product.price || 0
            let priceAmount = 0
            if (
              typeof price === 'object' &&
              price !== null &&
              'amount' in price
            ) {
              priceAmount = (price as { amount: number }).amount || 0
            } else if (typeof price === 'number') {
              priceAmount = price
            }
            existingProduct.count++
            existingProduct.totalPrice += priceAmount
          } else {
            const price = product.price || 0
            let priceAmount = 0
            if (
              typeof price === 'object' &&
              price !== null &&
              'amount' in price
            ) {
              priceAmount = (price as { amount: number }).amount || 0
            } else if (typeof price === 'number') {
              priceAmount = price
            }
            productMap.set(key, {
              ...product,
              count: 1,
              totalPrice: priceAmount,
            })
          }
        }
      })
    })
    return Array.from(productMap.values())
  })
  const getIconForProduct = (product: Product): string => {
    if (
      product.type === 'LU' ||
      product.code === 'LUGGAGE' ||
      product.code === 'SPECIAL_LUGGAGE'
    ) {
      return luggage
    } else if (product.type === 'BK' || product.code === 'BIKE') {
      return bike
    } else if (
      product.code?.includes('FEE') ||
      product.name?.includes('gebyr')
    ) {
      return fee
    } else {
      return ticket
    }
  }
  const totalFeesAmount = computed(() => {
    let feesTotal = 0
    confirmedFees.value.forEach((fee: Fee) => {
      const price = fee.price || 0
      if (typeof price === 'object' && price !== null && 'amount' in price) {
        feesTotal += (price as { amount: number }).amount || 0
      } else if (typeof price === 'number') {
        feesTotal += price
      }
    })
    return formatNormalizedPrice(normalizePrice(feesTotal))
  })
  const totalPrice = computed(() => {
    if (!booking.value) return '0,-'
    
    const totalPaid = booking.value.total_price_paid || 0
    
    return formatNormalizedPrice(normalizePrice(totalPaid))
  })
  const provisionalCancelledProductsTotal = computed(() => {
    let total = 0
    nonSeatProducts.value.forEach((product) => {
      total += product.totalPrice
    })
    return total
  })

  const totalWithoutFees = computed(() => {
    if (!booking.value) return '0,-'
    
    const totalPaid = booking.value.total_price_paid || 0
    
    let nonProvisionalFeesTotal = 0
    if (booking.value?.outbound_booking_tariff_segments) {
      booking.value.outbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          segment.fees?.forEach((fee: Fee) => {
            if (fee.provisional === false) {
              const price = fee.price || 0
              if (typeof price === 'object' && price !== null && 'amount' in price) {
                nonProvisionalFeesTotal += (price as { amount: number }).amount || 0
              } else if (typeof price === 'number') {
                nonProvisionalFeesTotal += price
              }
            }
          })
        }
      )
    }
    
    if (booking.value?.inbound_booking_tariff_segments) {
      booking.value.inbound_booking_tariff_segments.forEach(
        (segment: Segment) => {
          segment.fees?.forEach((fee: Fee) => {
            if (fee.provisional === false) {
              const price = fee.price || 0
              if (typeof price === 'object' && price !== null && 'amount' in price) {
                nonProvisionalFeesTotal += (price as { amount: number }).amount || 0
              } else if (typeof price === 'number') {
                nonProvisionalFeesTotal += price
              }
            }
          })
        }
      )
    }
    
    if (booking.value?.fees) {
      booking.value.fees.forEach((fee: Fee) => {
        if (fee.provisional === false) {
          const price = fee.price || 0
          if (typeof price === 'object' && price !== null && 'amount' in price) {
            nonProvisionalFeesTotal += (price as { amount: number }).amount || 0
          } else if (typeof price === 'number') {
            nonProvisionalFeesTotal += price
          }
        }
      })
    }
    
    return formatNormalizedPrice(normalizePrice(totalPaid - nonProvisionalFeesTotal))
  })
</script>
s
<style lang="scss" scoped>
  .my-past-travel {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    border-radius: $border-radius;
    background-color: $clr-primary-50;
    box-shadow: $box-shadow-card;
    margin-bottom: 2rem;

    &__title {
      @include title-large;
      color: $clr-primary;
    }

    &__journey {
      margin-bottom: 1rem;

      &--return {
        border-top: 1px solid $clr-primary-100;
        padding-top: 1rem;
      }
    }

    &__od {
      @include title-medium;
      margin-top: 0;
      margin-bottom: 0.5rem;
      color: inherit;
    }

    &__journey-type {
      @include body-medium;
      color: $clr-primary;
      margin-top: 0;
      margin-bottom: 0.5rem;
      font-weight: bold;
    }

    &__date {
      @include body-medium;
      margin-top: 0;
      margin-bottom: 0.25rem;
    }

    &__time {
      @include body-medium;
      margin-top: 0;
      margin-bottom: 0;
    }

    &__details {
      margin-top: 1.5rem;
      border-top: 1px solid $clr-primary-100;
      padding-top: 1rem;
    }

    &__detail {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      @include body-medium;

      span {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
    }

    &__icon {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;

      &.clr-secondary {
        color: $clr-secondary;
      }
    }

    &__total {
      display: flex;
      justify-content: space-between;
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid $clr-primary-100;
      @include title-small;
      font-weight: bold;
    }

    &__total-without-fees {
      display: flex;
      justify-content: space-between;
      margin-top: 0.5rem;
      @include body-medium;
      color: #ff00ff;
      font-style: italic;
    }
  }
</style>
